import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { autoinject } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {
    LookupValueRequest,
    LookupValueResponse,
    UpdateLookupValuesResponse,
    PersistLookupValuesRequest,
    TableColumnMetaDataResponse,
    TableMetaDataResponse,
    PersistLookupsValuesRequest,
    GetLookupResponse
} from "../../models/lookups/lookup.models";
import {Urls} from "../../constants/lookups/lookup.constants";
import { BindingEngine } from "aurelia-binding";
import { ArrayUtils } from "../../utilities/arrays";
import { Guid } from "../../utilities/guid";
import { CacheCategory } from "../../constants/lookups/lookup.constants";
import { DateHelper } from "../../utilities/date-helper";
import { AppInsightsService } from "@rightship-tech/common-ui/services/appinsights/appinsights-service";

@autoinject
export class LookupClientService {

    private cacheFlag: { flag: string, response: GetLookupResponse, successfull: boolean, subscribers: any[], expiryTimer: any, expiryTimeInSeconds: number }[] = [];
    private subscriptions: { query: string, params: string, func: () => void }[] = [];

    constructor(private requestClient: RequestClientService,
        private bindingEngine: BindingEngine,
        private cachingService: CacheClientService, private appInsightService:AppInsightsService) {
    }

    clearLookupCache(request: LookupValueRequest): Promise<any>;
    clearLookupCache(lookupName: string, parameters?: string): Promise<any>;

    public clearLookupCache(...args): Promise<any> {
        let request: LookupValueRequest;
        if (args && args[0] instanceof LookupValueRequest) {
            request = args[0] as LookupValueRequest;
        } else if (typeof args[0] == "string" && typeof args[1] == "string") {
            request = new LookupValueRequest(args[0], args[1]);
        } else if (typeof args[0] == "string") {
            request = new LookupValueRequest(args[0]);
        }
        return new Promise<void>((resolve, reject) => {
            this.cachingService.remove(request.getCacheKey()).then(() => {
                this.subscriptions.filter(y => y.query == request.lookupValueQuery && y.params == request.parameters).forEach(x => {
                    x.func();
                });
                resolve();
                return;
            }).catch(() => {
                reject();
                return;
            })
        });
    }

    clearLookupCacheFull(lookupName: string, parameters?: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.clearLookupCache(lookupName, parameters);
            this.requestClient.post(`${Urls.ClearCache}/${lookupName}`).then(() => resolve(),  () => reject());
        });
    }

    clearAllLookups(): Promise<void> {
        return this.cachingService.removeKeyContains(CacheCategory.lookups);
    }

    subscribeForLookupsRefresh(query, params, func) {
        if (!this.subscriptions) {
            this.subscriptions = [];
        }
        this.subscriptions.push({ query, params, func });
    }

    unsubscribeForLookupsRefresh(query, params, func) {
        if (this.subscriptions) {
            const index = ArrayUtils.findIndex(this.subscriptions, x => x.func == func && x.query == query && x.params == params);
            if (index > -1) {
                this.subscriptions.splice(index, 1);
            }
        }
    }

    public getLookup(getLookupRequest: LookupValueRequest, cacheFlagKey: string = "default", expiryTimeInSeconds: number = 20): Promise<LookupValueResponse> {
        
        return new Promise<LookupValueResponse>((resolve, reject) => {
            //This is a temporary log to identify the request calls for ports and users
            if(getLookupRequest.lookupValueQuery.toLowerCase() == ("ports") || getLookupRequest.lookupValueQuery.toLowerCase() == "users"){
                const url = window.location.href;
                this.appInsightService.logError("Lookup Client Service Called for ( "+ getLookupRequest.lookupValueQuery+" ) Url Location:" + url +
                    " Params: " + JSON.stringify(getLookupRequest.parameters));
            }

            if (getLookupRequest) {
                const found = this.cacheFlag.filter(x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey))[0];
                if (!found) { //We are NOT currently doing an identical call to the server, proceed
                    if (cacheFlagKey) {
                        this.cacheFlag.push({ flag: this.lookupRequestToString(getLookupRequest, cacheFlagKey), response: null, expiryTimer: null, expiryTimeInSeconds, successfull: null, subscribers: [] });
                    }
                    this.getData(getLookupRequest, cacheFlagKey).then(x => {
                        resolve(x);
                    }).catch(reject);
                } else { // We have an identical call currently processing, wait for it and return that data
                    this.waitForData(getLookupRequest, cacheFlagKey).then(x => {
                        resolve(new LookupValueResponse(x));
                    }).catch(reject);
                }
            } 
            else {
                reject();
            }
        });
    }

    private getData(getLookupRequest: LookupValueRequest, cacheFlagKey: string): Promise<LookupValueResponse> {
        return new Promise<LookupValueResponse>((resolve, reject) => {
            this.cachingService.get<GetLookupResponse>(getLookupRequest.getCacheKey(), CacheLocation.LocalStorage, false).then(cachedResponse => {
                if (!cachedResponse || !cachedResponse.values) {
                    cachedResponse = {
                        lastModifiedDate: null,
                        values: [],
                        lookupValueQuery: getLookupRequest.lookupValueQuery,
                        parameters: getLookupRequest.parameters,
                        defaultValue: null
                    }
                }
                getLookupRequest.clientLastModifiedDate = cachedResponse.lastModifiedDate;
                this.doRequest(getLookupRequest).then((response) => {
                    const func = () => {
                        const index = ArrayUtils.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
                        const cacheFlag = this.cacheFlag[index];
                        if (index > -1) {
                            cacheFlag.response = cachedResponse;
                            if (cacheFlag.subscribers.length == 0) {
                                this.cacheFlag.splice(index, 1);
                            }
                            cacheFlag.successfull = true;
                        }
                        resolve(new LookupValueResponse(cachedResponse));
                    };
                    if (!cachedResponse.lastModifiedDate || DateHelper.castDate(response.lastModifiedDate) > DateHelper.castDate(cachedResponse.lastModifiedDate)) {
                        this.cachingService.put(getLookupRequest.getCacheKey(), response).then(() => {
                            cachedResponse = response;
                            func();
                        })
                    } else {
                        func();
                    }
                }).catch(() => {
                    const index = ArrayUtils.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
                    const cacheFlag = this.cacheFlag[index];
                    if (index > -1) {
                        cacheFlag.response = null;
                        if (cacheFlag.subscribers.length == 0) {
                            this.cacheFlag.splice(index, 1);
                        }
                        cacheFlag.successfull = false;
                    }
                    reject();
                });
            });
        });
    }

    private waitForData(getLookupRequest: LookupValueRequest, cacheFlagKey: string): Promise<GetLookupResponse> {
        return new Promise<GetLookupResponse>((resolve, reject) => {
            const guid = Guid.generateGuid();
            const index = ArrayUtils.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
            const cacheFlag = this.cacheFlag[index];
            if (cacheFlag.successfull != null) {
                if (cacheFlag.successfull) {
                    resolve(cacheFlag.response);
                } else {

                    reject(cacheFlag.response);
                }
                return;
            }
            cacheFlag.subscribers.push(guid);
            const subscription = this.bindingEngine.propertyObserver(cacheFlag, "successfull")
                .subscribe(newValue => {
                    subscription.dispose();
                    if (newValue) {
                        resolve(cacheFlag.response);
                    } else {

                        reject(cacheFlag.response);
                    }
                    cacheFlag.subscribers.splice(cacheFlag.subscribers.indexOf(guid), 1);
                    if (cacheFlag.subscribers.length == 0) {
                        cacheFlag.expiryTimer = setTimeout(() => {
                            cacheFlag.expiryTimer = null;
                            this.cacheFlag.splice(ArrayUtils.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey)), 1);
                        }, cacheFlag.expiryTimeInSeconds * 1000);
                    }
                });
        });
    }

    private doRequest(getLookupRequest: LookupValueRequest): Promise<GetLookupResponse> {
        return this.requestClient.get<GetLookupResponse>(Urls.GetLookup, getLookupRequest);
    }

    public getTableMetadata(): Promise<TableMetaDataResponse> {
        return this.requestClient.get<TableMetaDataResponse>(Urls.GetTableMetaDatas, null);
    }

    public getLookupColumns(columnId: number): Promise<TableColumnMetaDataResponse> {
        return this.requestClient.get<TableColumnMetaDataResponse>(Urls.GetTableColumnMetaDatas + "/" + columnId + "/1/20");
    }

    public updateLookupValues(request: PersistLookupValuesRequest): Promise<UpdateLookupValuesResponse> {
        return new Promise<UpdateLookupValuesResponse>((resolve, reject) => {
            this.requestClient.post<UpdateLookupValuesResponse>(
                Urls.UpdateLookupValues, {}, request).then(x => {
                    if (x.exceptionNumber === 547) {
                        reject(x);
                    }
                    resolve(x);
                },  () => {
                    reject();
                });
        });
    }

    public updateLookupsOrdering(request: PersistLookupsValuesRequest): Promise<UpdateLookupValuesResponse> {
        return new Promise<UpdateLookupValuesResponse>((resolve, reject) => {
            this.requestClient.post<UpdateLookupValuesResponse>(
                Urls.UpdateLookupsOrdering, {}, request).then(response => {
                    if (response.exceptionNumber === 547) {
                        reject(response);
                    }
                    resolve(response);
                },  () => {
                    reject();
                });
        });
    }

    private lookupRequestToString(request: LookupValueRequest, cacheFlagKey: string): string {
        return request.lookupValueQuery + " " + request.parameters + " " + (cacheFlagKey ? (" " + cacheFlagKey) : "");
    }
}
